import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Field, Input, useFocusTrap } from '@truecare/ui';
import { ApiError, unwrap } from '@truecare/api-client';
import { api } from '../api';

/**
 * Phase 14c — TOTP re-enroll modal.
 *
 * Two flavours, selected by `isReEnrollment`:
 *
 * **First-time enrollment** (`isReEnrollment === false`):
 *   1. Display warning + Begin button
 *   2. POST /auth/totp/enroll (no step-up required for first enroll —
 *      the conditional TotpFreshOnReEnrollGuard skips the check when
 *      user.totpSecret === null)
 *   3. Render QR + secret + 6-digit input
 *   4. POST /auth/totp/verify with the new code
 *   5. Close modal + invalidate auth query
 *
 * **Re-enrollment** (`isReEnrollment === true`):
 *   1. Display warning that old TOTP will stop working
 *   2. Ask for current 6-digit code → POST /auth/totp-step-up
 *      (flips Session.totpSatisfiedAt; clears the
 *      TotpFreshOnReEnrollGuard for the next call)
 *   3. POST /auth/totp/enroll → returns new QR + secret
 *   4. Ask for new 6-digit code → POST /auth/totp/verify
 *   5. Close modal + invalidate auth query
 *
 * The conditional gating closes the Phase 4 vulnerability where
 * /auth/totp/enroll had only SessionGuard and immediately rotated the
 * secret — a stolen session could DoS the user's TOTP. The step-up
 * chain forces the operator to prove possession of the current device
 * before letting them rotate.
 */

interface EnrollResult {
  secret: string;
  otpauthUrl: string;
  qrCodeDataUrl: string;
}

type Step =
  | 'intro'
  | 'step-up'
  | 'scan-new'
  | 'verify-new'
  | 'done';

export interface TotpReEnrollModalProps {
  isReEnrollment: boolean;
  onClose: () => void;
}

export function TotpReEnrollModal({ isReEnrollment, onClose }: TotpReEnrollModalProps) {
  const queryClient = useQueryClient();
  // Audit H12 — this is a security-critical, operator-MANDATORY 2FA dialog, yet
  // it declared aria-modal without trapping focus, naming itself, or handling
  // Escape. A keyboard/screen-reader operator opening it had focus stay on the
  // background page (which AT is told is inert). Match the sibling
  // TotpStepUpModal: trap focus, close on Escape, and label the dialog.
  const panelRef = useRef<HTMLDivElement>(null);
  useFocusTrap(panelRef, { active: true, onEscape: onClose });
  const [step, setStep] = useState<Step>('intro');
  const [stepUpCode, setStepUpCode] = useState('');
  const [enrollResult, setEnrollResult] = useState<EnrollResult | null>(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function beginFlow(): Promise<void> {
    setError(null);
    if (isReEnrollment) {
      setStep('step-up');
    } else {
      await callEnroll();
    }
  }

  async function submitStepUp(): Promise<void> {
    setPending(true);
    setError(null);
    try {
      await unwrap(
        api.POST('/api/v1/auth/totp-step-up' as never, {
          body: { token: stepUpCode },
        } as never),
      );
      setStepUpCode('');
      await callEnroll();
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 429
          ? 'Too many attempts. Wait a moment and try again.'
          : 'That code is incorrect or has expired.',
      );
    } finally {
      setPending(false);
    }
  }

  async function callEnroll(): Promise<void> {
    setPending(true);
    try {
      const result = (await unwrap(
        api.POST('/api/v1/auth/totp/enroll' as never, {} as never),
      )) as unknown as EnrollResult;
      setEnrollResult(result);
      setStep('scan-new');
    } catch (err) {
      setError(
        err instanceof ApiError && err.message
          ? err.message
          : 'Could not start enrollment.',
      );
    } finally {
      setPending(false);
    }
  }

  async function submitVerify(): Promise<void> {
    setPending(true);
    setError(null);
    try {
      // Body shape note: /auth/totp/verify expects `{ code }` (TotpVerifyRequestSchema)
      // — distinct from /auth/totp-step-up which expects `{ token }` (TotpStepUpRequestSchema).
      // These differ by accident of phase ordering (Phase 4 vs Phase 14a) but the
      // contracts are locked.
      await unwrap(
        api.POST('/api/v1/auth/totp/verify' as never, {
          body: { code: verifyCode },
        } as never),
      );
      void queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
      setStep('done');
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 429
          ? 'Too many attempts. Wait a moment and try again.'
          : 'That code is incorrect. Try again.',
      );
    } finally {
      setPending(false);
    }
  }

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="totp-re-enroll-modal-title"
      data-testid="totp-re-enroll-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40"
    >
      <div className="w-full max-w-md rounded-md bg-white p-6 shadow-lg">
        <h2 id="totp-re-enroll-modal-title" className="font-heading text-lg text-forest">
          {isReEnrollment ? 'Re-enroll TOTP' : 'Enroll TOTP'}
        </h2>

        {step === 'intro' && (
          <>
            <Alert tone="warning" className="mt-4" data-testid="totp-intro-warning">
              {isReEnrollment
                ? "Re-enrolling will rotate your TOTP secret. Your current authenticator app's codes will stop working immediately. Have your phone ready to scan a new QR code."
                : 'You will be asked to scan a QR code with an authenticator app (Authy, Google Authenticator, 1Password, etc.) and enter the 6-digit code it generates.'}
            </Alert>
            <div className="mt-4 flex gap-3">
              <Button data-testid="totp-begin" onClick={beginFlow} disabled={pending}>
                {pending ? 'Starting…' : 'Begin'}
              </Button>
              <Button variant="ghost" onClick={onClose}>
                Cancel
              </Button>
            </div>
          </>
        )}

        {step === 'step-up' && (
          <>
            <p className="mt-4 text-sm text-ink">
              Enter the current 6-digit code from your authenticator to confirm.
            </p>
            <Field label="Current 6-digit code" id="totp-step-up-code" className="mt-3">
              <Input
                id="totp-step-up-code"
                data-testid="totp-step-up-code"
                inputMode="numeric"
                maxLength={6}
                value={stepUpCode}
                onChange={(e) => setStepUpCode(e.target.value.replace(/\D/g, ''))}
              />
            </Field>
            {error && (
              <Alert tone="danger" className="mt-3" data-testid="totp-step-up-error">
                {error}
              </Alert>
            )}
            <div className="mt-4 flex gap-3">
              <Button
                data-testid="totp-step-up-submit"
                onClick={submitStepUp}
                disabled={stepUpCode.length !== 6 || pending}
              >
                {pending ? 'Verifying…' : 'Verify'}
              </Button>
              <Button variant="ghost" onClick={onClose}>
                Cancel
              </Button>
            </div>
          </>
        )}

        {step === 'scan-new' && enrollResult && (
          <>
            <p className="mt-4 text-sm text-ink">
              Scan this QR code with your authenticator app, then enter the 6-digit code it
              generates.
            </p>
            <div className="mt-3 flex justify-center">
              <img
                src={enrollResult.qrCodeDataUrl}
                alt="TOTP enrollment QR"
                data-testid="totp-qr-image"
                className="h-48 w-48"
              />
            </div>
            <p className="mt-2 text-center font-mono text-xs text-ink-muted">
              Or enter the secret manually: <span data-testid="totp-secret">{enrollResult.secret}</span>
            </p>
            <Field label="New 6-digit code" id="totp-verify-code" className="mt-3">
              <Input
                id="totp-verify-code"
                data-testid="totp-verify-code"
                inputMode="numeric"
                maxLength={6}
                value={verifyCode}
                onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ''))}
              />
            </Field>
            {error && (
              <Alert tone="danger" className="mt-3" data-testid="totp-verify-error">
                {error}
              </Alert>
            )}
            <div className="mt-4 flex gap-3">
              <Button
                data-testid="totp-verify-submit"
                onClick={submitVerify}
                disabled={verifyCode.length !== 6 || pending}
              >
                {pending ? 'Verifying…' : 'Confirm'}
              </Button>
              <Button variant="ghost" onClick={onClose}>
                Cancel
              </Button>
            </div>
          </>
        )}

        {step === 'done' && (
          <>
            <Alert tone="success" className="mt-4" data-testid="totp-done-success">
              {isReEnrollment ? 'Re-enrollment complete.' : 'Enrollment complete.'}
            </Alert>
            <div className="mt-4 flex gap-3">
              <Button data-testid="totp-done-close" onClick={onClose}>
                Close
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
